import { getBetByName, getFullTimeOdds } from 'helpers/matchHelper';
import styles from './MatchMobile.module.css';
import { formatDateTime } from 'helpers/dateHelper';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MatchOffer } from 'types/MatchOffer';
import { useNavigate } from 'react-router-dom';
import { FULL_TIME_BETGROUP_NAME } from 'config/constants';
import { OtherBets } from 'components/OtherBets/OtherBets';
import { splitMarkets } from 'helpers/marketLines';
import { translateBetMarketName } from 'helpers/betI18n';
import { SlipContext } from 'context/slipContext';
import { getDefaultSportIconSrc } from 'helpers/sportIcon';
import { SportIcon } from 'components/ThemeIcon/SportIcon';
import { TeamCrest } from 'components/TeamCrest/TeamCrest';
import { MatchInsightsModal } from 'components/MatchInsights/MatchInsightsModal';
import { usePlayerLayout } from 'lib/playerLayout';
import { VipMatchRow } from '../VipMatchRow/VipMatchRow';
import { VipIcon } from '../VipChrome/VipIcons';

interface Props {
  match: MatchOffer;
  showFullMatchData?: boolean;
  betName?: string;
  isMultiLeagueList?: boolean;
}

/** VIP lists one line per match; a match's own page keeps the full block. */
export const MatchMobile = (props: Props) => {
  const vip = usePlayerLayout() === 'vip';
  return vip && !props.showFullMatchData ? (
    <VipMatchRow
      match={props.match}
      betName={props.betName}
      isMultiLeagueList={props.isMultiLeagueList}
    />
  ) : (
    <ClassicMatchMobile {...props} />
  );
};

const ClassicMatchMobile = ({
  match,
  showFullMatchData = false,
  betName,
  isMultiLeagueList = false,
}: Props) => {
  const [homeWinSelected, setHomeWinSelected] = useState(false);
  const [showInsights, setShowInsights] = useState(false);
  const [drawSelected, setDrawSelected] = useState(false);
  const [awayWinSelected, setAwayWinSelected] = useState(false);
  const { entries, addSlipEntry, removeSlipEntry, entryExist } =
    useContext(SlipContext);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const vip = usePlayerLayout() === 'vip';

  useEffect(() => {
    if (homeWin) {
      if (homeWinSelected && !entryExist(match, match.fullTimeBet, homeWin)) {
        setHomeWinSelected(false);
      } else if (
        !homeWinSelected &&
        entryExist(match, match.fullTimeBet, homeWin)
      )
        setHomeWinSelected(true);
    }

    if (draw) {
      if (drawSelected && !entryExist(match, match.fullTimeBet, draw)) {
        setDrawSelected(false);
      } else if (!homeWinSelected && entryExist(match, match.fullTimeBet, draw))
        setDrawSelected(true);
    }
    if (awayWin) {
      if (awayWinSelected && !entryExist(match, match.fullTimeBet, awayWin)) {
        setAwayWinSelected(false);
      } else if (
        !homeWinSelected &&
        entryExist(match, match.fullTimeBet, awayWin)
      )
        setAwayWinSelected(true);
    }
  }, [entries]);

  const handle1x2Click = (bet: '1' | 'Draw' | '2') => {
    const odd = match.fullTimeBet.odds!.filter((x) => x.name === bet)[0];

    if (
      (homeWinSelected && bet === '1') ||
      (drawSelected && bet === 'Draw') ||
      (awayWinSelected && bet === '2')
    ) {
      removeSlipEntry(match, match.fullTimeBet, odd);
    } else {
      addSlipEntry(match, match.fullTimeBet, odd);
    }
  };

  const dateTime = new Date(match.time * 1000);
  const { homeWin, draw, awayWin } = getFullTimeOdds(match);
  const homeName = match.home?.name ?? match.league?.name ?? 'Unknown';
  const awayName = match.away?.name ?? '-';

  let selectedBet = null;
  if (betName) {
    selectedBet = getBetByName(match, betName);
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.dataWrapper}>
        {isMultiLeagueList && (
          <div className={styles.leagueName}>{match.league.name}</div>
        )}
        <div
          className={styles.timeAndTeamsWrapper}
          onClick={() =>
            !showFullMatchData && navigate(`/sports-betting/match/${match.fi}`)
          }
        >
          <div className={styles.dateWrapper}>
            <span>
              {formatDateTime(dateTime, {
                removeSeconds: true,
                removeYear: true,
              })}
            </span>
          </div>
          {isMultiLeagueList && (
            <SportIcon
              className={styles.sportIcon}
              sportId={match.league.sport}
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = getDefaultSportIconSrc();
              }}
            />
          )}
          <div className={styles.teams} data-sport-icon={isMultiLeagueList}>
            <span>
              <TeamCrest
                imageId={match.home?.image_id}
                className={styles.crest}
              />
              {homeName}
            </span>
            <span>
              <TeamCrest
                imageId={match.away?.image_id}
                className={styles.crest}
              />
              {awayName}
            </span>
          </div>{' '}
          {!showFullMatchData && (
            <>
              <div className={styles.otherBetsCount}>
                {match.bets.length - 1}
              </div>
              <div className={styles.chevronRight} />
            </>
          )}
        </div>
      </div>
      {(!betName || betName === FULL_TIME_BETGROUP_NAME) && (
        <>
          <div className={styles.threeWayLabel}>
            <span>
              {!homeWin && !draw && !awayWin
                ? t('no_bet_offer_message')
                : translateBetMarketName(match.fullTimeBet, t)}
            </span>
            <button
              type="button"
              className={styles.statsButton}
              onClick={() => setShowInsights(true)}
            >
              {vip ? (
                <VipIcon name="chart" size={13} />
              ) : (
                <span aria-hidden="true">📊</span>
              )}
              {t('match_statistics_label')}
            </button>
          </div>
          {showInsights && (
            <MatchInsightsModal
              fi={match.fi}
              onClose={() => setShowInsights(false)}
            />
          )}
          {(homeWin || draw || awayWin) && (
            <div className={styles.betsWrapper}>
              {homeWin && (
                <div
                  className={homeWinSelected ? styles.betSelected : ''}
                  onClick={() => {
                    setHomeWinSelected(!homeWinSelected);
                    handle1x2Click('1');
                  }}
                >
                  <span>{t('bet_col_1x2_home')}</span>
                  <span>{homeWin.odds.toFixed(2)}</span>
                </div>
              )}
              {draw && (
                <div
                  className={drawSelected ? styles.betSelected : ''}
                  onClick={() => {
                    setDrawSelected(!drawSelected);
                    handle1x2Click('Draw');
                  }}
                >
                  <span>{t('bet_col_1x2_draw')}</span>
                  <span>{draw.odds.toFixed(2)}</span>
                </div>
              )}
              {awayWin && (
                <div
                  className={awayWinSelected ? styles.betSelected : ''}
                  onClick={() => {
                    setAwayWinSelected(!awayWinSelected);
                    handle1x2Click('2');
                  }}
                >
                  <span>{t('bet_col_1x2_away')}</span>
                  <span>{awayWin.odds.toFixed(2)}</span>
                </div>
              )}
            </div>
          )}
        </>
      )}
      {betName &&
        betName !== FULL_TIME_BETGROUP_NAME &&
        (match.marketsPending ? (
          <div className={styles.noOfferMessage}>{t('loading_label')}</div>
        ) : selectedBet ? (
          // As in the match's own page: a handicap one card per line with
          // 1 / X / 2, never "+3 (Neriješ..." cut short (owner, 2026-11-03).
          <OtherBets
            bets={splitMarkets([selectedBet], {
              home: match.home?.name,
              away: match.away?.name,
            })}
            match={match}
          />
        ) : (
          <div className={styles.noOfferMessage}>
            {t('no_bet_offer_message')}
          </div>
        ))}
    </div>
  );
};
